import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Form, Badge } from "react-bootstrap";
import {
  fetchClasses,
  deleteClass,
  clearError,
  clearSuccess,
} from "../../redux/slices/classSlice";
import { fetchBoards } from "../../redux/slices/boardSlice";
import ClassModal from "./ClassModal";
import InfoPill from "../Common/InfoPill";
import ConfirmModal from "../Common/ConfirmModal";
import DynamicBadge from "../Common/DynamicBadge";
import { FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const DATATABLE_PAGINATION_OPTIONS = {
  initialState: { rowsPerPage: 10, options: [5, 10, 20, 50] }
};

const SafeImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  return hasError || !src ? (
    <div
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "4px",
        backgroundColor: "#f8f9fa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #dee2e6"
      }}
    >
      <i className="bi bi-mortarboard text-secondary" style={{ fontSize: "1.2rem" }}></i>
    </div>
  ) : (
    <img
      src={src}
      alt={alt}
      style={{
        width: "40px",
        height: "40px",
        objectFit: "cover",
        borderRadius: "4px",
        border: "1px solid #dee2e6"
      }}
      onError={() => setHasError(true)}
    />
  );
};

const Classes = () => {
  const dispatch = useDispatch();
  const { classes = [], isLoading, error, success } = useSelector((state) => state.classes);
  const { boards = [] } = useSelector((state) => state.boards);

  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [boardFilter, setBoardFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    dispatch(fetchClasses());
    dispatch(fetchBoards());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  const handleEdit = React.useCallback((classData) => {
    setEditingClass(classData);
    setShowModal(true);
  }, []);

  const handleDeleteClick = React.useCallback((classData) => {
    setClassToDelete(classData);
    setShowConfirmModal(true);
  }, []);

  const handleConfirmDelete = React.useCallback(() => {
    if (classToDelete) {
      dispatch(deleteClass(classToDelete.id));
      setShowConfirmModal(false);
      setClassToDelete(null);
    }
  }, [classToDelete, dispatch]);

  const filteredClasses = React.useMemo(() => {
    return classes.filter((c) => {
      const matchesBoard = !boardFilter || c.board_id === boardFilter;
      const matchesStatus =
        statusFilter === "" ||
        Boolean(c.is_active) === (statusFilter === "true");
      return matchesBoard && matchesStatus;
    });
  }, [classes, boardFilter, statusFilter]);

  const header = React.useMemo(() => [
    {
      prop: "class_name",
      title: "Class Name",
      isFilterable: true,
      isSortable: true,
      cell: (row) => (
        <span className="fw-bold text-primary">{row.class_name}</span>
      )
    },
    {
      prop: "image_url",
      title: "Image",
      cell: (row) => (
        <SafeImage src={row.image_url} alt={row.class_name} />
      ),
    },
    {
      prop: "board_name",
      title: "Board",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <DynamicBadge text={row.board_name || "Unknown"} />
      ),
    },
    {
      prop: "is_active",
      title: "Status",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge bg={row.is_active ? "success" : "secondary"}>
          {row.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      prop: "createddate",
      title: "Created Date",
      isSortable: true,
      isFilterable: true,
      cell: (row) => formatDateToDDMMYYYY(row.createddate) || "-",
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button variant="outline-dark" size="sm" onClick={() => handleEdit(row)}>
            <FaPencilAlt />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteClick(row)}>
            <FaRegTrashAlt />
          </Button>
        </div>
      ),
    },
  ], [handleEdit, handleDeleteClick]);

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="text-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading classes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Classes
        </Link>
      </div>

      <DatatableWrapper
        body={filteredClasses}
        headers={header}
        paginationOptionsProps={DATATABLE_PAGINATION_OPTIONS}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1" style={{ maxWidth: "200px" }}>
              <Filter placeholder="Search classes..." className="w-100" />
            </div>
            <div style={{ maxWidth: "180px" }}>
              <Form.Select
                value={boardFilter}
                onChange={(e) => setBoardFilter(e.target.value)}
                className="btn-sm"
              >
                <option value="">All Boards</option>
                {boards.map((board) => (
                  <option key={board.id} value={board.id}>
                    {board.board_name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div style={{ maxWidth: "150px" }}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="btn-sm"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </div>
            <PaginationOptions />
            <InfoPill
              left="Total Classes"
              right={filteredClasses?.length}
            />
          </div>

          <div className="d-flex align-items-center">
            <Button
              variant="dark"
              className="btn-sm"
              onClick={() => {
                setEditingClass(null);
                setShowModal(true);
              }}
            >
              New Class
            </Button>
          </div>
        </div>

        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>

      <ClassModal show={showModal} onHide={() => setShowModal(false)} classData={editingClass} />

      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Class"
        message={`Are you sure you want to delete the class "${classToDelete?.class_name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={isLoading}
      />
    </div>
  );
};

export default Classes;
